/**
 * Rules Editor.
 *
 * The user's primary surface for managing auto-classification.
 * Collapsible card on the cashflow page with:
 *
 *   - Table of every rule (seed + user-added), sortable by priority
 *   - Enable / disable toggle (cheap to flip without deleting)
 *   - Inline edit (pattern, category, account scope, priority, match type)
 *   - Add new rule button with inline form
 *   - Delete (with confirm for seeds)
 *   - Tester input — type any text, see which rule(s) match and which
 *     wins. Saves a round-trip vs trial-and-error CSV uploads.
 *   - "Reclassify all" button to push rule changes back through every
 *     non-user-categorised transaction in one go.
 *
 * Why inline editing instead of a modal: the user is likely making
 * several edits in a row (working through the queue). Modals add
 * friction. The row expands in-place.
 */

import React, { useState, useMemo } from 'react';
import { toast } from 'react-toastify';
import { useCashFlow } from '../contexts/CashFlowContext';
import { BankRule } from '../types';
import { SEED_CATEGORIES, getCategoryLabel, getCategoryColour } from '../utils/categories';
import { classify } from '../utils/rulesEngine';

type EditState =
  | { kind: 'none' }
  | { kind: 'editing'; id: string }
  | { kind: 'creating' };

interface RuleDraft {
  matchType: 'contains' | 'startsWith' | 'regex';
  pattern: string;
  category: string;
  accountId: string; // '' means "all accounts"
  /** '' = don't touch scope; 'business'/'personal' = a match also
   *  assigns the books (how the mixed-use card auto-triages). */
  scope: string;
  priority: number;
  enabled: boolean;
}

const BLANK_DRAFT: RuleDraft = {
  matchType: 'contains',
  pattern: '',
  category: 'expense:other',
  accountId: '',
  scope: '',
  priority: 50,
  enabled: true,
};

function ruleToDraft(rule: BankRule): RuleDraft {
  return {
    matchType: rule.matchType,
    pattern: rule.pattern,
    category: rule.category,
    accountId: rule.accountId || '',
    scope: rule.scope || '',
    priority: rule.priority || 50,
    enabled: rule.enabled,
  };
}

/** Inline category picker — same option grouping as the page-level
 *  category selects so the user sees a consistent menu. */
function CategorySelect({ value, onChange, size = 'sm' }: {
  value: string;
  onChange: (next: string) => void;
  size?: 'sm' | 'md';
}) {
  return (
    <select
      className={`form-select${size === 'sm' ? ' form-select-sm' : ''}`}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      <optgroup label="Income">
        {SEED_CATEGORIES.filter((c) => c.bucket === 'income').map((c) => (
          <option key={c.id} value={c.id}>{c.label}</option>
        ))}
      </optgroup>
      <optgroup label="Expense">
        {SEED_CATEGORIES.filter((c) => c.bucket === 'expense').map((c) => (
          <option key={c.id} value={c.id}>{c.label}</option>
        ))}
      </optgroup>
    </select>
  );
}

function RuleForm({
  draft,
  setDraft,
  accountsOptions,
  onSave,
  onCancel,
  saving,
}: {
  draft: RuleDraft;
  setDraft: (d: RuleDraft) => void;
  accountsOptions: Array<{ id: string; label: string }>;
  onSave: () => void;
  onCancel: () => void;
  saving: boolean;
}) {
  return (
    <div className="row g-2 align-items-end p-3 bg-light rounded">
      <div className="col-md-2">
        <label className="form-label small mb-1">Match type</label>
        <select
          className="form-select form-select-sm"
          value={draft.matchType}
          onChange={(e) => setDraft({ ...draft, matchType: e.target.value as RuleDraft['matchType'] })}
        >
          <option value="contains">contains</option>
          <option value="startsWith">startsWith</option>
          <option value="regex">regex</option>
        </select>
      </div>
      <div className="col-md-2">
        <label className="form-label small mb-1">Pattern</label>
        <input
          type="text"
          className="form-control form-control-sm"
          value={draft.pattern}
          onChange={(e) => setDraft({ ...draft, pattern: e.target.value })}
          placeholder={draft.matchType === 'regex' ? '^TRANSFER FROM .+ INV-\\d+' : 'e.g. COLES or TRANSFER FROM '}
        />
      </div>
      <div className="col-md-2">
        <label className="form-label small mb-1">Category</label>
        <CategorySelect value={draft.category} onChange={(next) => setDraft({ ...draft, category: next })} />
      </div>
      <div className="col-md-2">
        <label className="form-label small mb-1">Account scope</label>
        <select
          className="form-select form-select-sm"
          value={draft.accountId}
          onChange={(e) => setDraft({ ...draft, accountId: e.target.value })}
        >
          <option value="">All accounts</option>
          {accountsOptions.map((a) => (
            <option key={a.id} value={a.id}>{a.label}</option>
          ))}
        </select>
      </div>
      <div className="col-md-1">
        <label className="form-label small mb-1">Books</label>
        <select
          className="form-select form-select-sm"
          value={draft.scope}
          onChange={(e) => setDraft({ ...draft, scope: e.target.value })}
          title="A match can also assign business/family books (leave as '—' to keep the account default)"
        >
          <option value="">—</option>
          <option value="business">business</option>
          <option value="personal">family</option>
        </select>
      </div>
      <div className="col-md-1">
        <label className="form-label small mb-1">Priority</label>
        <input
          type="number"
          className="form-control form-control-sm"
          value={draft.priority}
          onChange={(e) => setDraft({ ...draft, priority: Number(e.target.value) || 0 })}
          min={0}
          max={999}
          step={10}
        />
      </div>
      <div className="col-md-1">
        <label className="form-label small mb-1">Enabled</label>
        <div className="form-check form-switch mt-1">
          <input
            type="checkbox"
            className="form-check-input"
            checked={draft.enabled}
            onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })}
          />
        </div>
      </div>
      <div className="col-md-1 d-flex gap-1 justify-content-end">
        <button
          type="button"
          className="btn btn-sm btn-primary"
          onClick={onSave}
          disabled={!draft.pattern.trim() || saving}
          title="Save rule"
        >
          {saving ? '…' : 'Save'}
        </button>
        <button type="button" className="btn btn-sm btn-light" onClick={onCancel} disabled={saving}>
          Cancel
        </button>
      </div>
    </div>
  );
}

function RuleTester({ rules }: { rules: BankRule[] }) {
  const [text, setText] = useState('');
  const { accounts } = useCashFlow();
  const [accountId, setAccountId] = useState<string>('');

  const result = useMemo(() => {
    if (!text.trim()) return null;
    return classify(rules, accountId || accounts[0]?.id || '', text, text.toLowerCase());
  }, [text, accountId, accounts, rules]);

  // Also surface all matching rules (not just the winner) so the user
  // can see if multiple rules collide on the same input.
  const allMatches = useMemo(() => {
    if (!text.trim()) return [];
    const canonical = text.toLowerCase();
    return rules.filter((r) => {
      if (!r.enabled) return false;
      switch (r.matchType) {
        case 'contains':   return canonical.includes((r.pattern || '').toLowerCase());
        case 'startsWith': return canonical.startsWith((r.pattern || '').toLowerCase());
        case 'regex': {
          try { return new RegExp(r.pattern).test(text); } catch { return false; }
        }
        default: return false;
      }
    });
  }, [text, rules]);

  return (
    <div className="border rounded p-3 mb-3 bg-light-info bg-opacity-10">
      <div className="d-flex align-items-center gap-2 mb-2">
        <i className="bi bi-vial" />
        <strong className="small">Test a description against your rules</strong>
      </div>
      <div className="row g-2 align-items-end">
        <div className="col-md-7">
          <input
            type="text"
            className="form-control form-control-sm"
            placeholder="Paste a transaction description here…"
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
        </div>
        <div className="col-md-3">
          <select
            className="form-select form-select-sm"
            value={accountId}
            onChange={(e) => setAccountId(e.target.value)}
          >
            <option value="">Any account</option>
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>{a.displayName}</option>
            ))}
          </select>
        </div>
        <div className="col-md-2 text-end">
          {text.trim() && (
            <button type="button" className="btn btn-sm btn-light" onClick={() => setText('')}>
              Clear
            </button>
          )}
        </div>
      </div>
      {text.trim() && (
        <div className="mt-2 small">
          {result ? (
            <>
              Winner: <span
                className="badge ms-1"
                style={{
                  background: getCategoryColour(result.category) + '20',
                  color: getCategoryColour(result.category),
                  border: `1px solid ${getCategoryColour(result.category)}40`,
                }}
              >{getCategoryLabel(result.category)}</span>
              <span className="text-muted ms-2">via rule <code>{result.ruleId}</code></span>
              {allMatches.length > 1 && (
                <span className="text-muted ms-2">
                  ({allMatches.length} rules matched; highest priority wins)
                </span>
              )}
            </>
          ) : (
            <span className="text-muted">No rule matches — this would land in Uncategorised.</span>
          )}
        </div>
      )}
    </div>
  );
}

export function RulesEditor() {
  const { rules, accounts, addRule, updateRule, deleteRule, reclassifyAll } = useCashFlow();
  const [expanded, setExpanded] = useState(false);
  const [editState, setEditState] = useState<EditState>({ kind: 'none' });
  const [draft, setDraft] = useState<RuleDraft>(BLANK_DRAFT);
  const [saving, setSaving] = useState(false);
  const [reclassifying, setReclassifying] = useState(false);
  const [filter, setFilter] = useState('');

  const accountsOptions = useMemo(
    () => accounts.map((a) => ({ id: a.id, label: a.displayName })),
    [accounts],
  );

  const visibleRules = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const list = q
      ? rules.filter((r) =>
          (r.pattern || '').toLowerCase().includes(q) ||
          (r.category || '').toLowerCase().includes(q),
        )
      : rules;
    // Sort: priority desc, then pattern asc.
    return [...list].sort(
      (a, b) => (b.priority || 0) - (a.priority || 0) || (a.pattern || '').localeCompare(b.pattern || ''),
    );
  }, [rules, filter]);

  const beginCreate = () => {
    setDraft({ ...BLANK_DRAFT });
    setEditState({ kind: 'creating' });
  };
  const beginEdit = (rule: BankRule) => {
    setDraft(ruleToDraft(rule));
    setEditState({ kind: 'editing', id: rule.id });
  };
  const cancel = () => {
    setEditState({ kind: 'none' });
    setDraft(BLANK_DRAFT);
  };

  const save = async () => {
    if (!draft.pattern.trim()) return;
    setSaving(true);
    try {
      // Validate regex up-front so we catch typos before write.
      if (draft.matchType === 'regex') {
        try { new RegExp(draft.pattern); }
        catch (err: any) { throw new Error(`Invalid regex: ${err?.message || 'bad pattern'}`); }
      }
      const payload: any = {
        matchType: draft.matchType,
        pattern: draft.pattern.trim(),
        category: draft.category,
        priority: draft.priority,
        enabled: draft.enabled,
      };
      // Only include accountId/scope when set — Firestore writes don't
      // need empty-string fields hanging around.
      if (draft.accountId) payload.accountId = draft.accountId;
      if (draft.scope) payload.scope = draft.scope;

      if (editState.kind === 'creating') {
        await addRule(payload);
        toast.success('Rule created.');
      } else if (editState.kind === 'editing') {
        // Explicitly null out fields the user un-set.
        if (!draft.accountId) payload.accountId = null;
        if (!draft.scope) payload.scope = null;
        await updateRule(editState.id, payload);
        toast.success('Rule updated.');
      }
      cancel();
    } catch (e: any) {
      toast.error(e?.message || 'Failed to save rule');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (rule: BankRule) => {
    const isSeed = !!rule.isSeed;
    const msg = isSeed
      ? `Delete the seed rule "${rule.pattern}"? It won't come back unless you re-create it manually.`
      : `Delete the rule "${rule.pattern}"?`;
    if (!window.confirm(msg)) return;
    try {
      await deleteRule(rule.id);
      toast.success('Rule deleted.');
    } catch (e: any) {
      toast.error(e?.message || 'Failed to delete rule');
    }
  };

  const handleToggle = async (rule: BankRule) => {
    try {
      await updateRule(rule.id, { enabled: !rule.enabled });
    } catch (e: any) {
      toast.error(e?.message || 'Failed to toggle rule');
    }
  };

  const handleReclassify = async () => {
    if (!window.confirm(
      'Re-apply the current rules to every transaction you haven\'t manually categorised? Your manual overrides are kept.',
    )) return;
    setReclassifying(true);
    try {
      const { updated, scanned } = await reclassifyAll();
      toast.success(`Reclassified ${updated} of ${scanned} transactions.`);
    } catch (e: any) {
      toast.error(e?.message || 'Reclassify failed');
    } finally {
      setReclassifying(false);
    }
  };

  return (
    <div className="card mb-4">
      <div
        className="card-header py-3 d-flex justify-content-between align-items-center"
        style={{ cursor: 'pointer' }}
        onClick={() => setExpanded((v) => !v)}
      >
        <h6 className="mb-0">
          <i className={`bi bi-chevron-${expanded ? 'down' : 'right'} me-2`} />
          Rules <span className="text-muted small">({rules.length})</span>
        </h6>
        <span className="text-muted small">
          {expanded ? 'Click to collapse' : 'Click to manage auto-classification'}
        </span>
      </div>
      {expanded && (
        <div className="card-body" onClick={(e) => e.stopPropagation()}>
          {/* Top toolbar */}
          <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
            <button
              type="button"
              className="btn btn-sm btn-primary"
              onClick={beginCreate}
              disabled={editState.kind !== 'none'}
            >
              <i className="bi bi-plus-lg me-1" /> Add rule
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline-primary"
              onClick={handleReclassify}
              disabled={reclassifying}
              title="Re-run all rules against every transaction that hasn't been manually overridden"
            >
              {reclassifying ? 'Reclassifying…' : (
                <><i className="bi bi-arrow-repeat me-1" /> Reclassify all</>
              )}
            </button>
            <div className="ms-auto" style={{ maxWidth: 280, flex: '1 1 200px' }}>
              <input
                type="search"
                className="form-control form-control-sm"
                placeholder="Filter rules by pattern or category…"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
              />
            </div>
          </div>

          {/* Rule tester */}
          <RuleTester rules={rules} />

          {/* Create form */}
          {editState.kind === 'creating' && (
            <div className="mb-3">
              <RuleForm
                draft={draft}
                setDraft={setDraft}
                accountsOptions={accountsOptions}
                onSave={save}
                onCancel={cancel}
                saving={saving}
              />
            </div>
          )}

          {/* Rules table */}
          <div className="table-responsive">
            <table className="table table-sm align-middle mb-0">
              <thead>
                <tr>
                  <th style={{ width: 60 }}>On</th>
                  <th style={{ width: 100 }}>Match</th>
                  <th>Pattern</th>
                  <th style={{ width: 200 }}>Category</th>
                  <th style={{ width: 140 }}>Scope</th>
                  <th style={{ width: 80 }} className="text-end">Priority</th>
                  <th style={{ width: 150 }}></th>
                </tr>
              </thead>
              <tbody>
                {visibleRules.length === 0 && (
                  <tr><td colSpan={7} className="text-center text-muted py-4">
                    {filter ? 'No rules match the filter.' : 'No rules yet — click Add rule to get started.'}
                  </td></tr>
                )}
                {visibleRules.map((rule) => {
                  const isEditing = editState.kind === 'editing' && editState.id === rule.id;
                  if (isEditing) {
                    return (
                      <tr key={rule.id}>
                        <td colSpan={7}>
                          <RuleForm
                            draft={draft}
                            setDraft={setDraft}
                            accountsOptions={accountsOptions}
                            onSave={save}
                            onCancel={cancel}
                            saving={saving}
                          />
                        </td>
                      </tr>
                    );
                  }
                  const scopeLabel = rule.accountId
                    ? (accounts.find((a) => a.id === rule.accountId)?.displayName || rule.accountId)
                    : 'All';
                  return (
                    <tr key={rule.id} className={rule.enabled ? '' : 'text-muted'}>
                      <td>
                        <div className="form-check form-switch m-0">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={rule.enabled}
                            onChange={() => handleToggle(rule)}
                            title={rule.enabled ? 'Disable rule' : 'Enable rule'}
                          />
                        </div>
                      </td>
                      <td className="small">{rule.matchType}</td>
                      <td className="small"><code>{rule.pattern}</code>{rule.isSeed && (
                        <span className="badge bg-light-secondary text-secondary ms-2" title="Built-in seed rule">seed</span>
                      )}</td>
                      <td>
                        <span
                          className="badge"
                          style={{
                            background: getCategoryColour(rule.category) + '20',
                            color: getCategoryColour(rule.category),
                            border: `1px solid ${getCategoryColour(rule.category)}40`,
                          }}
                        >{getCategoryLabel(rule.category)}</span>
                      </td>
                      <td className="small">{scopeLabel}</td>
                      <td className="text-end small">{rule.priority}</td>
                      <td className="text-end">
                        <button
                          type="button"
                          className="btn btn-sm btn-light me-1"
                          onClick={() => beginEdit(rule)}
                          disabled={editState.kind !== 'none'}
                          title="Edit rule"
                        >
                          <i className="bi bi-pencil" />
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleDelete(rule)}
                          disabled={editState.kind !== 'none'}
                          title="Delete rule"
                        >
                          <i className="bi bi-trash" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default RulesEditor;
